import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BarChart3 } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const ROASCalculator = () => {
  const [revenue, setRevenue] = useState<string>("");
  const [adSpend, setAdSpend] = useState<string>("");
  const [result, setResult] = useState<{ roas: number; roasRatio: string } | null>(null);

  const calculateROAS = () => {
    const revenueNum = parseFloat(revenue) || 0;
    const adSpendNum = parseFloat(adSpend) || 0;
    
    if (adSpendNum === 0) {
      setResult(null);
      return;
    }

    const roas = (revenueNum / adSpendNum) * 100;
    const roasRatio = (revenueNum / adSpendNum).toFixed(2);
    
    setResult({ roas, roasRatio: `${roasRatio}:1` });
  };

  const reset = () => {
    setRevenue("");
    setAdSpend("");
    setResult(null);
  };

  const getROASAssessment = (roas: number) => {
    if (roas >= 400) return { label: "Excellent", color: "text-green-600" };
    if (roas >= 300) return { label: "Good", color: "text-green-500" };
    if (roas >= 200) return { label: "Average", color: "text-yellow-600" };
    if (roas >= 100) return { label: "Break-even", color: "text-orange-500" };
    return { label: "Losing Money", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("roas-calculator");

  return (
    <ToolPageLayout
      title="ROAS Calculator"
      description="Calculate your Return on Ad Spend to measure the effectiveness of your advertising campaigns and optimize your marketing budget."
      metaDescription="ROAS (Return on Ad Spend) measures the revenue generated for every dollar spent on advertising. Unlike ROI, ROAS focuses specifically on advertising efficiency, making it essential for paid media optimization."
      icon={BarChart3}
      slug="roas-calculator"
      formula={{
        equation: "ROAS = (Revenue from Ads / Ad Spend) × 100",
        variables: [
          { name: "Revenue from Ads", description: "Total revenue attributed to your advertising campaigns" },
          { name: "Ad Spend", description: "Total amount spent on advertising" },
        ]
      }}
      example={{
        inputs: [
          { label: "Revenue from Ads", value: "$15,000" },
          { label: "Ad Spend", value: "$5,000" },
        ],
        output: "300% ROAS (3:1)",
        interpretation: "For every $1 spent on ads, you generated $3 in revenue. This is generally considered a good ROAS for most industries."
      }}
      improvements={[
        "Refine audience targeting to reach higher-intent users",
        "A/B test ad creatives to improve click-through rates",
        "Optimize landing pages to improve conversion rates",
        "Implement proper attribution to accurately measure ad impact",
        "Adjust bidding strategies based on performance data",
        "Focus budget on top-performing campaigns and channels",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Paid Marketing", href: "/paid-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate ROAS</CardTitle>
          <CardDescription>Enter your ad revenue and spend</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="revenue">Revenue from Ads ($)</Label>
            <Input
              id="revenue"
              type="number"
              placeholder="15000"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adSpend">Total Ad Spend ($)</Label>
            <Input
              id="adSpend"
              type="number"
              placeholder="5000"
              value={adSpend}
              onChange={(e) => setAdSpend(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculateROAS} className="flex-1">
              Calculate ROAS
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Your ROAS</p>
                <p className={`text-4xl font-bold ${getROASAssessment(result.roas).color}`}>
                  {result.roas.toFixed(0)}%
                </p>
                <p className="text-lg font-medium text-foreground mt-2">
                  {result.roasRatio}
                </p>
                <p className={`text-sm mt-3 ${getROASAssessment(result.roas).color}`}>
                  {getROASAssessment(result.roas).label}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default ROASCalculator;
